import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWorkspaceMetadata } from '../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { ScoopButton, ScoopText } from '../../ui'
import { ScoopSpinner } from '../../ui'
import { ROUTES } from '../../../router/routes'
import { CheckCircleIcon } from '@phosphor-icons/react'
import styles from './BillingFlow.module.scss'

/**
 * Success page shown after completing Chargebee checkout.
 * Polls for account tier update from webhook, then redirects.
 */
export const Success = () => {
    const navigate = useNavigate()
    const { refetch } = useWorkspaceMetadata()
    const [status, setStatus] = useState('processing') // 'processing' | 'success' | 'timeout'

    useEffect(() => {
        let intervalId
        let pollCount = 0 // Local variable instead of state to avoid re-renders
        const maxPolls = 15 // 30 seconds total (15 polls * 2 seconds)

        const pollForUpdate = async () => {
            try {
                console.log('[Success] Polling for tier update...', { pollCount })
                const { data: metadata } = await refetch()

                console.log('[Success] Current account:', {
                    accountType: metadata?.account?.accountType,
                    chargebeeCustomerId: metadata?.account?.chargebeeCustomerId,
                })

                // Check if upgraded (no longer free_trial)
                if (metadata?.account?.accountType !== 'free_trial') {
                    console.log('[Success] Tier updated! Redirecting...')
                    setStatus('success')
                    clearInterval(intervalId) // Stop polling immediately

                    // Wait 2 seconds for user to see success message
                    setTimeout(() => {
                        navigate(ROUTES.INSIGHTS)
                    }, 2000)

                    return true
                }
                return false
            } catch (error) {
                console.error('[Success] Error polling for account update:', error)
                return false
            }
        }

        const startPolling = async () => {
            // Initial check
            const updated = await pollForUpdate()
            if (updated) return

            // Start polling interval
            intervalId = setInterval(async () => {
                pollCount++ // Increment local variable (no re-render)
                const updated = await pollForUpdate()

                if (updated || pollCount >= maxPolls) {
                    clearInterval(intervalId)
                    if (!updated && pollCount >= maxPolls) {
                        console.log('[Success] Polling timed out after 30 seconds')
                        setStatus('timeout')
                    }
                }
            }, 2000)
        }

        startPolling()

        return () => {
            if (intervalId) clearInterval(intervalId)
        }
    }, [refetch, navigate])

    return (
        <div className={styles.pageContainer}>
            <div className={styles.card}>
                {status === 'processing' && (
                    <>
                        <ScoopSpinner size={48} />
                        <ScoopText className={styles.title}>
                            Activating your subscription...
                        </ScoopText>
                        <ScoopText className={styles.subtitle}>
                            This should only take a moment
                        </ScoopText>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircleIcon size={64} weight="fill" className={styles.successIcon} />
                        <ScoopText className={styles.title}>Subscription Active!</ScoopText>
                        <ScoopText className={styles.subtitle}>
                            Redirecting to your workspace...
                        </ScoopText>
                    </>
                )}

                {status === 'timeout' && (
                    <>
                        <ScoopText className={styles.title}>Processing Your Upgrade...</ScoopText>
                        <ScoopText className={styles.message}>
                            This is taking longer than expected. Your account will be updated
                            shortly. You can close this page and check your Account Settings in a
                            few minutes, or contact support if the issue persists.
                        </ScoopText>
                        <div className={styles.buttonGroup}>
                            <ScoopButton
                                variant="outlined"
                                onClick={() => window.location.reload()}
                            >
                                Check Again
                            </ScoopButton>
                            <ScoopButton
                                variant="contained"
                                onClick={() => navigate(ROUTES.ACCOUNT)}
                            >
                                Go to Account Settings
                            </ScoopButton>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
